import React from 'react';
import { Alert, Avatar, Box, Button, Chip, TextField, Tooltip, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import { FirewallRegel, FirewallTeamMitglied, FirewallVorlage } from '../../services/firewallService';
import {
  colorsDisagree,
  COMMENT_MAX_LENGTH,
  commentRequired,
  FirewallFarbe,
  RATING_ORDER,
  RATING_SUBTITLE,
  RATING_TITLE,
  submitHint,
  vorlageLabel,
  vorlageText,
} from '../../utils/firewallReview';
import { ratingTone, SwipePalette, useSwipePalette } from './firewallSwipeTheme';

const RATING_ICON: Record<FirewallFarbe, React.ReactNode> = {
  rot: <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />,
  gelb: <QuestionMarkRoundedIcon sx={{ fontSize: 18 }} />,
  gruen: <CheckRoundedIcon sx={{ fontSize: 18 }} />,
};

const RatingOption: React.FC<{
  farbe: FirewallFarbe;
  selected: boolean;
  disabled?: boolean;
  onSelect: (farbe: FirewallFarbe) => void;
  testId: string;
  palette: SwipePalette;
}> = ({ farbe, selected, disabled, onSelect, testId, palette }) => {
  const tone = ratingTone(palette, farbe);
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      data-testid={testId}
      data-selected={selected ? 'true' : undefined}
      onClick={() => { if (!disabled) onSelect(farbe); }}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(farbe);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1.1,
        borderRadius: 2,
        border: `${selected ? 2 : 1.5}px solid ${selected ? tone.color : tone.border}`,
        bgcolor: tone.bg,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        boxShadow: selected ? `0 0 0 3px ${tone.bg}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': disabled ? undefined : { borderColor: tone.color },
        '&:focus-visible': { outline: `2px solid ${tone.color}`, outlineOffset: 2 },
      }}
    >
      <Avatar sx={{ width: 30, height: 30, bgcolor: tone.color, color: farbe === 'gelb' ? '#3b2f00' : '#fff' }}>
        {RATING_ICON[farbe]}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: tone.color, lineHeight: 1.25 }}>{RATING_TITLE[farbe]}</Typography>
        <Typography sx={{ fontSize: '0.74rem', color: palette.muted, lineHeight: 1.3 }}>{RATING_SUBTITLE[farbe]}</Typography>
      </Box>
      {selected && <CheckRoundedIcon sx={{ fontSize: 20, color: tone.color }} />}
    </Box>
  );
};

export const RatingCards: React.FC<{
  chosen: FirewallFarbe | null;
  disabled?: boolean;
  onSelect: (farbe: FirewallFarbe) => void;
  testIdPrefix: string;
  palette: SwipePalette;
}> = ({ chosen, disabled, onSelect, testIdPrefix, palette }) => (
  <Box role="radiogroup" aria-label="Bewertung" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {RATING_ORDER.map((farbe) => (
      <RatingOption
        key={farbe}
        farbe={farbe}
        selected={chosen === farbe}
        disabled={disabled}
        onSelect={onSelect}
        testId={`${testIdPrefix}-${farbe}`}
        palette={palette}
      />
    ))}
  </Box>
);

export interface CommentFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required: boolean;
  hint: string | null;
  inputRef: React.Ref<HTMLTextAreaElement>;
  testId: string;
  palette: SwipePalette;
  placeholder?: string;
}

export const CommentField: React.FC<CommentFieldProps> = ({ label, value, onChange, required, hint, inputRef, testId, palette, placeholder }) => {
  const empty = value.trim().length === 0;
  const showRequired = required && empty;
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: palette.text }}>{label}</Typography>
        {required && (
          <Typography data-testid="firewall-comment-required" sx={{ fontSize: '0.72rem', color: showRequired ? palette.red : palette.muted }}>
            Pflichtfeld
          </Typography>
        )}
      </Box>
      <TextField
        inputRef={inputRef}
        inputProps={{ 'data-testid': testId, maxLength: COMMENT_MAX_LENGTH, 'aria-required': required || undefined }}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        fullWidth
        multiline
        minRows={4}
        placeholder={placeholder ?? 'Bewertung begründen ...'}
        error={Boolean(hint) || showRequired}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: palette.tile,
            fontSize: '0.88rem',
            '& fieldset': { borderColor: palette.tileBorder },
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, gap: 1 }}>
        <Typography data-testid="firewall-comment-hint" sx={{ fontSize: '0.72rem', color: palette.red, minHeight: 16 }}>
          {hint ?? ''}
        </Typography>
        <Typography data-testid="firewall-comment-counter" sx={{ fontSize: '0.72rem', color: palette.muted, whiteSpace: 'nowrap' }}>
          {value.length} / {COMMENT_MAX_LENGTH}
        </Typography>
      </Box>
    </Box>
  );
};

export const SnippetGrid: React.FC<{
  vorlagen: FirewallVorlage[];
  sprache: string | null | undefined;
  onInsert: (snippet: string) => void;
  palette: SwipePalette;
}> = ({ vorlagen, sprache, onInsert, palette }) => {
  if (vorlagen.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: palette.text, mb: 0.75 }}>Textbausteine</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        {vorlagen.map((vorlage) => (
          <Button
            key={vorlage.id}
            data-testid="firewall-snippet"
            size="small"
            variant="outlined"
            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => onInsert(vorlageText(vorlage, sprache))}
            sx={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              fontSize: '0.76rem',
              fontWeight: 600,
              color: palette.text,
              borderColor: palette.tileBorder,
              bgcolor: palette.tile,
              lineHeight: 1.25,
              py: 0.9,
              '&:hover': { borderColor: palette.green, bgcolor: palette.tileHover },
              '& .MuiButton-startIcon': { mr: 0.75, color: palette.muted },
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {vorlageLabel(vorlage, sprache)}
            </Box>
          </Button>
        ))}
      </Box>
    </Box>
  );
};

const Footnote: React.FC<{ palette: SwipePalette; text: string }> = ({ palette, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center', mt: 1.5 }}>
    <LockOutlinedIcon sx={{ fontSize: 14, color: palette.faint }} />
    <Typography sx={{ fontSize: '0.72rem', color: palette.faint }}>{text}</Typography>
  </Box>
);

const PanelFrame: React.FC<{ palette: SwipePalette; testId: string; children: React.ReactNode }> = ({ palette, testId, children }) => (
  <Box
    data-testid={testId}
    sx={{
      p: 2,
      borderRadius: 3,
      bgcolor: palette.card,
      border: `1px solid ${palette.cardBorder}`,
      color: palette.text,
      position: { md: 'sticky' },
      top: { md: 12 },
    }}
  >
    {children}
  </Box>
);

export interface SwipeRatingPanelProps {
  chosen: FirewallFarbe | null;
  text: string;
  hint: string | null;
  error: string | null;
  saving: boolean;
  vorlagen: FirewallVorlage[];
  sprache: string | null | undefined;
  commentRef: React.Ref<HTMLTextAreaElement>;
  onSelect: (farbe: FirewallFarbe) => void;
  onTextChange: (value: string) => void;
  onInsertSnippet: (snippet: string) => void;
  onSubmit: () => void;
}

/** Rechtes Panel „Deine Bewertung“ im Swipe. */
export const SwipeRatingPanel: React.FC<SwipeRatingPanelProps> = ({
  chosen,
  text,
  hint,
  error,
  saving,
  vorlagen,
  sprache,
  commentRef,
  onSelect,
  onTextChange,
  onInsertSnippet,
  onSubmit,
}) => {
  const palette = useSwipePalette();
  const blocker = submitHint(chosen, text);
  return (
    <PanelFrame palette={palette} testId="firewall-rating-panel">
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.2 }}>Deine Bewertung</Typography>
      <Typography sx={{ fontSize: '0.78rem', color: palette.muted, mb: 1.5 }}>Regel prüfen und begründen</Typography>
      <RatingCards chosen={chosen} disabled={saving} onSelect={onSelect} testIdPrefix="firewall-rate" palette={palette} />
      <CommentField
        label="Kommentar"
        value={text}
        onChange={onTextChange}
        required={commentRequired(chosen)}
        hint={hint}
        inputRef={commentRef}
        testId="firewall-swipe-comment"
        palette={palette}
      />
      <SnippetGrid vorlagen={vorlagen} sprache={sprache} onInsert={onInsertSnippet} palette={palette} />
      {error && (
        <Alert data-testid="firewall-comment-error" severity="warning" sx={{ mt: 1.5, py: 0 }}>
          {error}
        </Alert>
      )}
      <Button
        data-testid="firewall-submit"
        fullWidth
        variant="contained"
        disabled={saving || Boolean(blocker)}
        onClick={onSubmit}
        endIcon={<ArrowForwardIcon />}
        sx={{ mt: 2, py: 1.1, fontWeight: 700, fontSize: '0.9rem' }}
      >
        Bewerten &amp; weiter
      </Button>
      <Typography data-testid="firewall-submit-hint" sx={{ fontSize: '0.72rem', color: blocker ? palette.muted : palette.green, textAlign: 'center', mt: 0.75, minHeight: 16 }}>
        {blocker ?? 'Bereit zum Speichern.'}
      </Typography>
      <Footnote palette={palette} text="Änderungen werden mit der Bewertung gespeichert." />
    </PanelFrame>
  );
};

export interface FinalRatingPanelProps {
  rule: FirewallRegel;
  team: FirewallTeamMitglied[];
  chosen: FirewallFarbe | null;
  text: string;
  hint: string | null;
  error: string | null;
  saving: boolean;
  vorlagen: FirewallVorlage[];
  sprache: string | null | undefined;
  commentRef: React.Ref<HTMLTextAreaElement>;
  onSelect: (farbe: FirewallFarbe) => void;
  onTextChange: (value: string) => void;
  onInsertSnippet: (snippet: string) => void;
  onAdopt: (farbe: FirewallFarbe, text: string) => void;
  onSubmit: () => void;
}

/** Rechtes Panel im Finalen Check: alle Pentester-Bewertungen, Übernehmen, Konsens. */
export const FinalRatingPanel: React.FC<FinalRatingPanelProps> = ({
  rule,
  team,
  chosen,
  text,
  hint,
  error,
  saving,
  vorlagen,
  sprache,
  commentRef,
  onSelect,
  onTextChange,
  onInsertSnippet,
  onAdopt,
  onSubmit,
}) => {
  const palette = useSwipePalette();
  const blocker = submitHint(chosen, text);
  const disagreement = colorsDisagree(rule.bewertungen.map((item) => item.farbe));
  const members = team.length > 0
    ? team
    : rule.bewertungen.map((item) => ({ id: item.mitarbeiter_id, name: item.name, initialen: item.initialen ?? '?' }));
  const open = members.filter((member) => !rule.bewertungen.some((item) => item.mitarbeiter_id === member.id));
  return (
    <PanelFrame palette={palette} testId="firewall-final-panel">
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.2 }}>Finaler Check</Typography>
      <Typography sx={{ fontSize: '0.78rem', color: palette.muted, mb: 1.5 }}>Bewertungen abgleichen und Konsens festlegen</Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>Bewertungen der Pentester</Typography>
        {disagreement && (
          <Chip
            data-testid="firewall-final-disagreement"
            size="small"
            label="Farben weichen ab"
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, color: palette.amber, border: `1px solid ${palette.amberBorder}`, bgcolor: palette.amberBg }}
          />
        )}
        {rule.weicht_vom_final_ab && (
          <Chip size="small" label="Nach dem Konsens geändert" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, color: palette.amber, border: `1px solid ${palette.amberBorder}`, bgcolor: palette.amberBg }} />
        )}
      </Box>
      <Box data-testid="firewall-peer-ratings" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rule.bewertungen.length === 0 && (
          <Typography sx={{ fontSize: '0.8rem', color: palette.muted }}>Noch keine Einzelbewertungen.</Typography>
        )}
        {rule.bewertungen.map((bewertung) => {
          const tone = ratingTone(palette, bewertung.farbe);
          const member = team.find((item) => item.id === bewertung.mitarbeiter_id);
          const comment = (bewertung.text ?? '').trim();
          return (
            <Box
              key={bewertung.mitarbeiter_id}
              data-testid="firewall-peer-rating"
              data-farbe={bewertung.farbe}
              sx={{
                borderRadius: 2,
                border: `1px solid ${tone.border}`,
                borderLeft: `4px solid ${tone.color}`,
                bgcolor: palette.tile,
                p: 1.25,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', fontWeight: 800, bgcolor: tone.color, color: bewertung.farbe === 'gelb' ? '#3b2f00' : '#fff' }}>
                  {member?.initialen ?? bewertung.initialen ?? '?'}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {member?.name ?? bewertung.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: tone.color, fontWeight: 700 }}>
                    {RATING_TITLE[bewertung.farbe]}
                    {bewertung.nach_konsens && (
                      <Box component="span" sx={{ color: palette.amber, fontWeight: 600 }}> · nach Konsens geändert</Box>
                    )}
                  </Typography>
                </Box>
                <Tooltip title={comment ? 'Kommentar in den Konsens übernehmen' : 'Kein Kommentar vorhanden'} arrow>
                  <span>
                    <Button
                      data-testid="firewall-adopt-comment"
                      size="small"
                      variant="outlined"
                      disabled={saving || !comment}
                      startIcon={<ContentPasteGoIcon sx={{ fontSize: '15px !important' }} />}
                      onClick={() => onAdopt(bewertung.farbe, comment)}
                      sx={{ fontSize: '0.72rem', py: 0.3, px: 1, borderColor: palette.tileBorder, color: palette.text, whiteSpace: 'nowrap' }}
                    >
                      Übernehmen
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <Typography
                data-testid="firewall-peer-comment"
                sx={{ mt: 0.75, fontSize: '0.8rem', whiteSpace: 'pre-wrap', color: comment ? palette.text : palette.faint, fontStyle: comment ? 'normal' : 'italic' }}
              >
                {comment || 'Ohne Kommentar'}
              </Typography>
            </Box>
          );
        })}
        {open.length > 0 && (
          <Typography data-testid="firewall-final-open" sx={{ fontSize: '0.74rem', color: palette.muted }}>
            Noch offen: {open.map((member) => member.name).join(', ')}
          </Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, mt: 2, mb: 1 }}>Konsens</Typography>
      {rule.konsens && (
        <Typography data-testid="firewall-consensus-chip" sx={{ fontSize: '0.72rem', color: palette.muted, mb: 1 }}>
          Bisher: {RATING_TITLE[rule.konsens.farbe]}
          {rule.konsens.gesetzt_am ? ` · ${new Date(rule.konsens.gesetzt_am).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
        </Typography>
      )}
      <RatingCards chosen={chosen} disabled={saving} onSelect={onSelect} testIdPrefix="firewall-final" palette={palette} />
      <CommentField
        label="Konsens-Kommentar"
        value={text}
        onChange={onTextChange}
        required={commentRequired(chosen)}
        hint={hint}
        inputRef={commentRef}
        testId="firewall-final-comment"
        palette={palette}
        placeholder="Kommentar übernehmen oder selbst begründen ..."
      />
      <SnippetGrid vorlagen={vorlagen} sprache={sprache} onInsert={onInsertSnippet} palette={palette} />
      {error && (
        <Alert data-testid="firewall-comment-error" severity="warning" sx={{ mt: 1.5, py: 0 }}>
          {error}
        </Alert>
      )}
      <Button
        data-testid="firewall-final-save"
        fullWidth
        variant="contained"
        disabled={saving || Boolean(blocker)}
        onClick={onSubmit}
        endIcon={<ArrowForwardIcon />}
        sx={{ mt: 2, py: 1.1, fontWeight: 700, fontSize: '0.9rem' }}
      >
        Final speichern &amp; weiter
      </Button>
      <Typography data-testid="firewall-submit-hint" sx={{ fontSize: '0.72rem', color: blocker ? palette.muted : palette.green, textAlign: 'center', mt: 0.75, minHeight: 16 }}>
        {blocker ?? 'Bereit zum Speichern.'}
      </Typography>
      <Footnote palette={palette} text="Die Einzelkommentare bleiben unverändert; gespeichert wird nur der Konsens." />
    </PanelFrame>
  );
};
